import { useParams, Link } from "react-router-dom";
import { allShows } from "@/mocks/varietyShows";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import ShowCard from "@/pages/home/components/ShowCard";
import { WebPageSchema, BreadcrumbListSchema } from "@/components/feature/StructuredData";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const showId = parseInt(id || "", 10);
  const show = allShows.find((s) => s.id === showId);

  const relatedShows = allShows
    .filter((s) => s.id !== showId)
    .slice(0, 6);

  const fakeEpisodes = Array.from({ length: 12 }, (_, i) => ({
    ep: i + 1,
    title: `第${i + 1}期`,
    duration: `${40 + Math.floor(Math.random() * 40)}分钟`,
    date: `2026-0${7 - Math.floor(i / 4)}-${String(30 - i).padStart(2, "0")}`,
  }));

  if (!show) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
          <h2 className="text-xl font-semibold text-foreground-700 mb-2">节目未找到</h2>
          <p className="text-foreground-400 mb-6">该节目不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <WebPageSchema
        name={`${show.title} - 红桃影院综艺在线观看`}
        url={`https://hongtao-movie.com/article/${id}`}
        description={`${show.title}，${show.description || `${show.category}类热门综艺节目`}。红桃影院高清在线观看，流畅不缓冲。`}
      />
      <BreadcrumbListSchema
        items={[
          { name: "首页", url: "https://hongtao-movie.com" },
          { name: show.category, url: `https://hongtao-movie.com/category/zhenrenxiu` },
          { name: show.title, url: `https://hongtao-movie.com/article/${id}` },
        ]}
      />

      <Breadcrumb />

      <main className="w-full px-4 md:px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <article>
            <div className="flex flex-col lg:flex-row gap-8 mb-12">
              <div className="flex-shrink-0 w-full lg:w-[300px]">
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={show.imageUrl}
                    alt={show.title}
                    title={show.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-primary-100 text-primary-600 text-xs font-medium rounded-full">
                    {show.category}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-accent-500">
                    <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i>
                    <strong>{show.rating}</strong>
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  {show.title}
                </h1>

                <p className="text-foreground-600 leading-relaxed mb-6">
                  {show.description || `${show.title}是一档${show.category}类综艺节目，每周精彩不断。高清画质流畅观看，带你感受综艺的魅力。`}
                </p>

                <div className="flex items-center gap-6 mb-6 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                    {show.views} 次观看
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                    {show.updateInfo}
                  </span>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-play-fill w-4 h-4 flex items-center justify-center"></i>
                    立即播放
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-heart-line w-4 h-4 flex items-center justify-center"></i>
                    收藏
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>

            <section className="mb-12">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                剧集列表
                <span className="text-sm font-normal text-foreground-400 ml-2">共{fakeEpisodes.length}期</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {fakeEpisodes.map((ep) => (
                  <div
                    key={ep.ep}
                    className="flex items-center justify-between p-4 bg-background-100 rounded-xl border border-background-200/60 hover:border-primary-200 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                        <i className="ri-play-fill text-sm"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">{ep.title}</p>
                        <p className="text-xs text-foreground-400">{ep.date}</p>
                      </div>
                    </div>
                    <span className="text-xs text-foreground-400">{ep.duration}</span>
                  </div>
                ))}
              </div>
            </section>
          </article>

          <section>
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-xl font-bold text-foreground-900">
                相关推荐
              </h2>
              <Link
                to="/"
                className="hidden sm:flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                更多推荐
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {relatedShows.map((related) => (
                <ShowCard
                  key={related.id}
                  id={related.id}
                  title={related.title}
                  category={related.category}
                  imageUrl={related.imageUrl}
                  rating={related.rating}
                  updateInfo={related.updateInfo}
                  views={related.views}
                />
              ))}
            </div>
          </section>
        </div>
      </main>
    </>
  );
}